import type { ComponentProps, ReactNode } from 'react'

import { Link, type LinkProps } from '@tanstack/react-router'

import { cx } from '@/libs/cva'

import { Button } from '../button/button'
import { Container } from '../container/container'
import { Heading } from '../heading/heading'
import { Text } from '../text/text'

interface PageSectionProps {
  title: string
  desc?: string
  children?: ReactNode
  actions?: ReactNode
  className?: string
}

/**
 * @deprecated
 */
function PageSectionRoot({
  title,
  desc,
  children,
  actions,
  className,
}: PageSectionProps) {
  return (
    <Container className={cx('divide-y p-0', className)}>
      <div className="flex items-center justify-between px-6 py-4">
        <div>
          <Heading level="h2">{title}</Heading>
          {desc && (
            <Text className="text-ui-fg-subtle" size="small">
              {desc}
            </Text>
          )}
        </div>
        {actions}
      </div>
      {children}
    </Container>
  )
}

interface PageSectionItemProps extends ComponentProps<'div'> {
  divide?: boolean
  grid?: boolean
}
function PageSectionItem({
  className,
  divide = false,
  grid = false,
  ...rest
}: PageSectionItemProps) {
  return (
    <div
      {...rest}
      className={cx(
        // 'px-6 py-4',
        {
          'divide-y divide-ui-border-menu-bot': divide,
          'grid grid-cols-1 gap-x-2 gap-y-4 md:grid-cols-3': grid,
        },
        className
      )}
    />
  )
}

interface PageActionsProps {
  actions: [
    {
      label: string
      to?: LinkProps['to']
      onClick?: () => void
      icon?: ReactNode
    },
  ]
}

function PageActions({ actions }: PageActionsProps) {
  return (
    <div className="flex items-center justify-center gap-x-2">
      {actions.map(({ label, to, onClick, icon }) => {
        const inner = to ? (
          <Link to={to}>
            {icon} {label}
          </Link>
        ) : (
          <>
            {icon} {label}
          </>
        )

        return (
          <Button
            key={label}
            variant="secondary"
            size="small"
            onClick={onClick}
            asChild={!!to}
          >
            {inner}
          </Button>
        )
      })}
    </div>
  )
}

interface PageSectionRowProps {
  title: string
  value?: ReactNode
  actions?: ReactNode
}

function PageSectionRow({ title, value, actions }: PageSectionRowProps) {
  const isValueString = typeof value === 'string' || !value

  return (
    <div
      className={cx(
        `grid w-full grid-cols-2 items-center gap-4 px-6 py-4 text-ui-fg-subtle`,
        {
          'grid-cols-[1fr_1fr_28px]': !!actions,
        }
      )}
    >
      <Text size="small" weight="plus" leading="compact">
        {title}
      </Text>

      {isValueString ? (
        <Text size="small" leading="compact" className="whitespace-pre-line text-pretty">
          {value ?? '-'}
        </Text>
      ) : (
        <div className="flex flex-wrap gap-1">{value}</div>
      )}

      {actions && <div>{actions}</div>}
    </div>
  )
}

/**
 * @deprecated
 */
export const PageSection = Object.assign(
  {},
  {
    Root: PageSectionRoot,
    Item: PageSectionItem,
    Actions: PageActions,
    Row: PageSectionRow,
  }
)
